import React, { useState, useEffect, useMemo } from "react";
import { Spin, Tabs, Tooltip, Typography } from "antd";
import { isUndefined } from "lodash";
import AnyaIcon from "ac-components/ac-icons/AnyaIcon";
import ACAIBadge from "ac-components/ac-ai-badge/ACAIBadge";
import { FindingDetailsDescription } from "./FindingDetailsDescription";
import RemediationAction from "./RemediationAction";
import MoreInformation from "./MoreInformation";
import AISummaryTab from "./AISummaryTab";
import AnyaAgentsTab from "./AnyaAgentsTab/AnyaAgentsTab";
import { useFindingDetails } from "./FindingDetailsProvider";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import { useGlobalSettingsContext } from "contexts/GlobalSettingsContext";
import { getFindingDetailsOverviewLastTab, setFindingDetailsOverviewLastTab } from "utils/localStorageUtils";

const getRemediationTabLabel = ({ aiRemediationTab, isAnyaGloballyEnabled, featureFlags }) => {
  const showAnyaIcon = !isUndefined(isAnyaGloballyEnabled) &&
    !isUndefined(aiRemediationTab?.enabled) &&
    isAnyaGloballyEnabled &&
    aiRemediationTab?.enabled &&
    !featureFlags.AI_ANYA_REMEDIATION_ACTIONS_DISABLED &&
    featureFlags.AI_ANYA_REMEDIATION_ACTIONS;

  const labelContent = showAnyaIcon ? (
    <>
      <AnyaIcon />
      Remediation Details
    </>
  ) : (!isUndefined(isAnyaGloballyEnabled) && !isUndefined(aiRemediationTab?.enabled)) ? (
    <>
      Remediation Details
      <ACAIBadge className="m-l-xs" type="remediationAction" />
    </>
  ) : null;

  if (aiRemediationTab?.enabled) {
    return <span className="d-flex align-items-center">{labelContent}</span>;
  }

  return (
    <span>
      <Tooltip
        title={
          <>
            Remediation Actions Disabled: Please enable from Global Settings to
            receive guidance on finding remediation.
            <Typography.Link
              target="blank"
              className="link-hover"
              onClick={(e) => e.stopPropagation()}
              href={"#/globalsettings?activeStep=step27"}
            >
              Update Settings
            </Typography.Link>
          </>
        }
        placement="top"
      >
        <span className="d-flex align-items-center" style={{ opacity: 0.5 }}>
          {labelContent}
        </span>
      </Tooltip>
    </span>
  );
};

const OverviewTabContent = ({ aiRemediationTab, loading }) => {
  const [activeTab, setActiveTab] = useState(() => getFindingDetailsOverviewLastTab());
  const { id, findingCustomFields } = useFindingDetails();
  const featureFlags = useFeatureFlag();
  const { anyaAIAssistantEnabled: isAnyaGloballyEnabled, aiSummaryEnabled } = useGlobalSettingsContext();

  const hasCustomFields = findingCustomFields && Object.keys(findingCustomFields).length > 0;
  const isOldViewEnabled = featureFlags.FINDING_DETAIL_OLD_VIEW;
  const isAnyaAgentsEnabled = featureFlags.AI_ANYA_AGENT;

  // Single tab configuration — FF conditions defined once, used for both
  // rendering and default tab fallback.
  const tabs = useMemo(() => [
    isAnyaAgentsEnabled && {
      key: "anyaAgents",
      label: (
        <span className="d-flex align-items-center">
          <AnyaIcon />
          Agents
        </span>
      ),
      content: <AnyaAgentsTab />,
    },
    {
      key: "details",
      label: "Details",
      content: <FindingDetailsDescription showCustomFields={isOldViewEnabled} />,
    },
    !isAnyaAgentsEnabled && aiSummaryEnabled && {
      key: "aiSummary",
      label: (
        <span className="d-flex align-items-center">
          <AnyaIcon />
          AI Summary
        </span>
      ),
      content: <AISummaryTab />,
    },
    !isOldViewEnabled && hasCustomFields && {
      key: "moreInfo",
      label: "More Information",
      content: <MoreInformation />,
    },
    !isAnyaAgentsEnabled && {
      key: "remediation",
      label: getRemediationTabLabel({ aiRemediationTab, isAnyaGloballyEnabled, featureFlags }),
      disabled: !aiRemediationTab?.enabled || loading,
      content: <RemediationAction key={id} />,
    },
  ].filter(Boolean), [isOldViewEnabled, isAnyaAgentsEnabled, aiSummaryEnabled, hasCustomFields, aiRemediationTab, isAnyaGloballyEnabled, featureFlags, loading, id]);

  // Fallback to "details" tab if current tab is unavailable
  useEffect(() => {
    if (loading) return;
    const availableKeys = tabs.map((t) => t.key);
    if (!availableKeys.includes(activeTab)) {
      setActiveTab("details");
    }
  }, [loading, activeTab, tabs]);

  useEffect(() => {
    if (!loading) {
      setFindingDetailsOverviewLastTab(activeTab);
    }
  }, [loading, activeTab]);

  return (
    <div className="overview-subtabs-container">
      <Spin spinning={loading}>
        <Tabs size="small" activeKey={activeTab} onChange={setActiveTab} destroyInactiveTabPane>
          {tabs.map(({ key, label, content, disabled }) => (
            <Tabs.TabPane tab={label} key={key} disabled={disabled}>
              {content}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </Spin>
    </div>
  );
};

export default OverviewTabContent;
